import type { PropsWithChildren } from 'react'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'

import { LocalStorageKeys } from '@/consts/localStorage.ts'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { openDialog } from '@/models/dialogs'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { useDesktopAppListener } from '@/modules/desktop/useDesktopAppListener.ts'
import { BACKEND_SERVER } from '@/utils/constants.ts'
import { noop } from '@/utils/misc.ts'

import { DESKTOP_APP_VERSION_TO_FORCE_UPDATE } from './consts'
import { cloneEvent } from './utils'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Intercom: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    electronAPI: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    grecaptcha: any
    addedListeners: boolean
  }
}

export interface DesktopAppContextProps {
  isApp: boolean
  appVersion: string | null
  lastPublishedAppVersion: string | null
  sendCustomAppAction: (action: string, data?: unknown) => void
  startedSessions: Map<string, Session>
}

export interface Session {
  id: string
  profileId: number
  creatorId: number
  status: 'Starting' | 'Running' | 'Error' | 'Done'
  error?: string
  pusherChannelName?: string
}

const DesktopAppContext = createContext<DesktopAppContextProps>({
  isApp: false,
  appVersion: null,
  lastPublishedAppVersion: null,
  sendCustomAppAction: noop,
  startedSessions: new Map(),
})

export const useDesktopApp = () => {
  return useContext(DesktopAppContext)
}

const sendLoggedOutAction = () => {
  sendCustomAppAction('logged-out', { hi: '', backendUrl: BACKEND_SERVER })
}

const sendLoggedInAction = (accessToken: string) => {
  sendCustomAppAction('logged-in', {
    token: accessToken,
    backendUrl: BACKEND_SERVER,
  })
}

export const sendCustomAppAction = (action: string, data?: unknown) => {
  window.electronAPI?.sendToMainProcess('custom-app-action', {
    action,
    data: data ?? {},
  })
}

export const DesktopAppProvider = ({ children }: PropsWithChildren) => {
  const [lastSentAccessToken, setLastSentAccessToken] = useState<string | null>(
    null,
  )
  const storageSubscribe = (listener: () => void) => {
    window.addEventListener('storage', listener)

    return () => void window.removeEventListener('storage', listener)
  }
  const desktopAppVersion = useSyncExternalStore(storageSubscribe, () =>
    localStorage.getItem(LocalStorageKeys.CREATORHERO_APP_VERSION),
  )
  const lastPublishedAppVersion = useSyncExternalStore(storageSubscribe, () =>
    localStorage.getItem(
      LocalStorageKeys.CREATORHERO_APP_LAST_PUBLISHED_VERSION,
    ),
  )
  const [startedSessions, setStartedSessions] = useState<Map<string, Session>>(
    new Map(),
  )
  const { isLoggedIn, checkedAuthentication, accessToken } = useAccount()

  // Listening for desktop app events
  const foundElectronApi = useDesktopAppListener(
    'session-update',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ([sessionString]: any[]) => {
      const session = JSON.parse(sessionString) as Session

      if (session.status === 'Error' || session.status === 'Done') {
        setStartedSessions((prev) => {
          const newSessions = new Map(prev)

          newSessions.delete(session.id)

          return newSessions
        })

        return
      }

      setStartedSessions((prev) => {
        const newSessions = new Map(prev)

        newSessions.set(session.id, session)

        return newSessions
      })
    },
    () => {
      sendCustomAppAction('initiate')
    },
  )

  // Sending authentication updates to desktop app
  useEffect(() => {
    if (!window.electronAPI || !checkedAuthentication) {
      return
    }
    if (!isLoggedIn || !accessToken) {
      sendLoggedOutAction()
      if (lastSentAccessToken != null) {
        setLastSentAccessToken(null)
      }

      return
    }
    if (lastSentAccessToken !== accessToken) {
      sendLoggedInAction(accessToken)
      setLastSentAccessToken(accessToken)
    }
  }, [isLoggedIn, accessToken, checkedAuthentication, lastSentAccessToken])

  const isApp = useMemo(
    () => foundElectronApi && desktopAppVersion != null,
    [foundElectronApi, desktopAppVersion],
  )

  useEffect(() => {
    if (isApp) {
      document.addEventListener(
        'click',
        (event: MouseEvent) => {
          if (event.shiftKey) {
            event.preventDefault()
            const eventClone = cloneEvent(event)

            eventClone.shiftKey = false
            event.target?.dispatchEvent(new PointerEvent('click', eventClone))
          }
        },
        true,
      )
    }
  }, [isApp])

  // TODO: Remove this once we no longer receive error log from users
  useEffect(() => {
    if (isApp && desktopAppVersion === DESKTOP_APP_VERSION_TO_FORCE_UPDATE) {
      openDialog({
        type: DIALOG_TYPE.DESKTOP_UPDATE_DIALOG,
      })
    }
  }, [isApp, desktopAppVersion])

  const values: DesktopAppContextProps = useMemo(
    () => ({
      isApp,
      appVersion: desktopAppVersion,
      lastPublishedAppVersion,
      sendCustomAppAction: sendCustomAppAction,
      startedSessions: startedSessions,
    }),
    [desktopAppVersion, isApp, startedSessions, lastPublishedAppVersion],
  )

  return (
    <DesktopAppContext.Provider value={values}>
      {children}
    </DesktopAppContext.Provider>
  )
}
